import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import type { ReactNode } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import type { TemplateField } from '../../../overview/models/template.types';
import type { TemplateFieldControlKind, TemplateFormValues } from '../../models/templateRuntime';
import { digitCountOf, NUMBER_MAX_DIGITS } from '../../models/templateRuntime';
import { TemplateCheckbox, TemplateRadioGroup, TemplateToggle } from './TemplateChoiceInputs';
import { TemplateListInput } from './TemplateListInput';
import { TemplateFileInput } from './TemplateFileInput';
import type { TemplateFileFormats } from '../../models/templateFile';
import { useEntityDatePattern } from '@/shared/hooks/useEntityDisplayFormatOptional';
import { calendarDateFormat, calendarDayToApi, toCalendarDay } from '@/shared/utils/dateUtils';

interface TemplateFieldInputProps {
  kind: TemplateFieldControlKind;
  id: string;
  labelId: string;
  field: TemplateField;
  options: readonly string[];
  rhf: ControllerRenderProps<TemplateFormValues, string>;
  invalid: boolean;
  describedBy?: string;
  disabled: boolean;
  /** The required marker, for the checkbox, whose label sits beside the box. */
  marker: ReactNode;
  /** How a Date is drawn: the masters' glyph inside the box, or Organization Setup's calendar cap. */
  dateStyle?: TemplateDateStyle;
  /** The earliest day the calendar lets the user pick; earlier days are disabled. */
  minDate?: Date;
  /** The box's placeholder in place of the type's own; absent, the type decides. */
  placeholder?: string;
  /** A File upload's allowed formats (a Purchase Request's); absent, any format. */
  fileFormats?: TemplateFileFormats;
}

export type TemplateDateStyle = 'masters' | 'organization';

/**
 * Whether putting `inserted` into the number box -- over its selection, if any -- would leave it holding more than
 * {@link NUMBER_MAX_DIGITS} digits.
 */
function exceedsDigits(target: EventTarget, inserted: string): boolean {
  if (!(target instanceof HTMLInputElement)) return false;
  const text = target.value;
  const start = target.selectionStart ?? text.length;
  const end = target.selectionEnd ?? text.length;
  const next = text.slice(0, start) + inserted + text.slice(end);
  return digitCountOf(next) > NUMBER_MAX_DIGITS;
}

/**
 * The input one control kind is drawn as -- the same PrimeReact components, with
 * the same props, that the masters' record form (`MasterFieldControl`) uses, so a
 * template's form looks like every other form in the application. Styled by the
 * masters sheet through the surrounding `ms-field`; the lists and the calendar
 * open as `ms-panel` panels.
 *
 * Each kind holds the value TYPE it submits -- a string, a `number | null`, a
 * `boolean` or a `string[]` -- and never a stringified one, so a Number reaches the
 * wire as a JSON number and a Checkbox as a JSON boolean. A Date is held as
 * `yyyy-MM-dd` and only drawn in the entity's date format.
 */
export function TemplateFieldInput({
  kind,
  id,
  labelId,
  field,
  options,
  rhf,
  invalid,
  describedBy,
  disabled,
  marker,
  dateStyle = 'masters',
  minDate,
  placeholder,
  fileFormats,
}: TemplateFieldInputProps) {
  /* The path is a string, so RHF cannot narrow the value; the field's type decides it. */
  const value: unknown = rhf.value;
  const text = typeof value === 'string' ? value : '';
  const list = Array.isArray(value) ? (value as string[]) : [];
  const invalidClass = invalid ? 'p-invalid' : undefined;
  const aria = { 'aria-invalid': invalid, 'aria-required': field.mandatory, 'aria-describedby': describedBy } as const;
  /*
    A Date is drawn in the selected entity's date format -- the entity's own, else its organization's Site
    Configuration -- and in the platform default (DD/MM/YYYY) before either is known (2026-10-14). It is still held
    and sent as the calendar day, `yyyy-MM-dd`, the same in every browser zone.
  */
  const dateFormat = calendarDateFormat(useEntityDatePattern());

  switch (kind) {
    case 'textarea':
      return (
        <InputTextarea
          ref={rhf.ref}
          id={id}
          rows={3}
          value={text}
          placeholder={placeholder}
          /*
            Inert as read-only, not disabled (2026-10-14): a disabled textarea takes no wheel, touch or keyboard
            scrolling, so a long Registered Address could not be read past its third line on Supplier's View. Read-only
            it still refuses edits, and scrolls inside its box (masterScreen.css `.p-inputtextarea[readonly]`).
          */
          readOnly={disabled}
          aria-readonly={disabled}
          className={invalidClass}
          onBlur={rhf.onBlur}
          onChange={(event) => {
            rhf.onChange(event.target.value);
          }}
          {...aria}
        />
      );

    case 'number':
    case 'currency': {
      const input = (
        <InputNumber
          inputRef={rhf.ref}
          inputId={id}
          value={typeof value === 'number' ? value : null}
          disabled={disabled}
          useGrouping={false}
          minFractionDigits={kind === 'currency' ? 2 : 0}
          maxFractionDigits={kind === 'currency' ? 2 : 6}
          placeholder={placeholder ?? `Enter ${field.label}`}
          inputClassName={invalidClass}
          onBlur={rhf.onBlur}
          onValueChange={(event) => {
            rhf.onChange(typeof event.value === 'number' ? event.value : null);
          }}
          {...aria}
        />
      );
      if (kind === 'currency') return input;
      /*
        A Number takes up to twelve digits (the builder's "Up to 12 digits"). The keystroke or paste that would go
        past them is refused before the box sees it -- in the capture phase, so PrimeReact's own handlers never run --
        rather than accepted and clamped, which would change what the user typed.
      */
      return (
        <span
          className="contents"
          onKeyDownCapture={(event) => {
            if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) return;
            if (exceedsDigits(event.target, event.key)) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onPasteCapture={(event) => {
            if (exceedsDigits(event.target, event.clipboardData.getData('text'))) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          {input}
        </span>
      );
    }

    case 'date':
      if (dateStyle === 'organization') {
        /* Organization Setup's Start Date: the calendar cap on the right, the entity's date format, its own panel. */
        return (
          <Calendar
            inputRef={rhf.ref}
            inputId={id}
            value={toCalendarDay(text)}
            disabled={disabled}
            showIcon
            icon="pi pi-calendar"
            dateFormat={dateFormat}
            placeholder={placeholder ?? 'Select date'}
            className={invalid ? 'template-date--org p-invalid' : 'template-date--org'}
            panelClassName="template-date-panel--org"
            minDate={minDate}
            onBlur={rhf.onBlur}
            onChange={(event) => {
              rhf.onChange(event.value instanceof Date ? calendarDayToApi(event.value) : '');
            }}
            {...aria}
          />
        );
      }
      return (
        <Calendar
          inputRef={rhf.ref}
          inputId={id}
          value={toCalendarDay(text)}
          disabled={disabled}
          showIcon
          readOnlyInput
          panelClassName="ms-panel"
          dateFormat={dateFormat}
          placeholder={placeholder ?? 'Select Date'}
          inputClassName={invalidClass}
          onBlur={rhf.onBlur}
          onChange={(event) => {
            rhf.onChange(event.value instanceof Date ? calendarDayToApi(event.value) : '');
          }}
          {...aria}
        />
      );

    case 'checkbox':
      return (
        <TemplateCheckbox
          id={id}
          inputRef={rhf.ref}
          label={field.label}
          checked={value === true}
          disabled={disabled}
          required={field.mandatory}
          invalid={invalid}
          describedBy={describedBy}
          marker={marker}
          onBlur={rhf.onBlur}
          onChange={rhf.onChange}
        />
      );

    case 'toggle':
      return (
        <TemplateToggle
          id={id}
          inputRef={rhf.ref}
          label={field.label}
          on={value === true}
          disabled={disabled}
          required={field.mandatory}
          invalid={invalid}
          describedBy={describedBy}
          onBlur={rhf.onBlur}
          onChange={rhf.onChange}
        />
      );

    case 'select':
      return (
        <Dropdown
          focusInputRef={rhf.ref}
          inputId={id}
          value={text === '' ? null : text}
          options={[...options]}
          disabled={disabled}
          /*
            Organization Setup's Country (2026-10-14): searchable whatever its length, the magnifier on the right, the
            labels alone on the standard panel.
          */
          filter
          panelClassName="ms-panel ms-panel--std"
          placeholder={placeholder ?? `Select ${field.label}`}
          dropdownIcon="pi pi-chevron-down"
          /* Clearable whenever editable, mandatory too: a cleared mandatory field says "{label} is required". */
          showClear={!disabled}
          className={invalidClass}
          onBlur={rhf.onBlur}
          onChange={(event) => {
            /* The clear button arrives as undefined, which is '' -- "not answered". */
            rhf.onChange(event.value === null || event.value === undefined ? '' : String(event.value));
          }}
          {...aria}
        />
      );

    case 'radio':
      return (
        <TemplateRadioGroup
          id={id}
          inputRef={rhf.ref}
          labelId={labelId}
          options={options}
          value={text}
          disabled={disabled}
          required={field.mandatory}
          invalid={invalid}
          describedBy={describedBy}
          onBlur={rhf.onBlur}
          onChange={rhf.onChange}
        />
      );

    case 'checklist':
      return (
        <MultiSelect
          inputId={id}
          value={list}
          options={[...options]}
          disabled={disabled}
          display="chip"
          filter
          panelClassName="ms-panel ms-panel--std"
          dropdownIcon="pi pi-chevron-down"
          showSelectAll={false}
          placeholder={`Select ${field.label}`}
          className={invalidClass}
          onBlur={rhf.onBlur}
          onChange={(event) => {
            /* Kept in the template's own order, whatever order they were ticked in. */
            const chosen = new Set(event.value as string[]);
            rhf.onChange(options.filter((option) => chosen.has(option)));
          }}
          {...aria}
        />
      );

    case 'multi-text':
      return (
        <TemplateListInput
          inputRef={rhf.ref}
          id={id}
          value={list}
          disabled={disabled}
          describedBy={describedBy}
          invalid={invalid}
          onBlur={rhf.onBlur}
          onChange={rhf.onChange}
        />
      );

    case 'upload':
      return (
        <TemplateFileInput
          id={id}
          label={field.label}
          value={text}
          disabled={disabled}
          invalid={invalid}
          describedBy={describedBy}
          formats={fileFormats}
          onChange={(next) => {
            rhf.onChange(next);
          }}
        />
      );

    default:
      return (
        <InputText
          ref={rhf.ref}
          id={id}
          type={kind === 'email' ? 'email' : kind === 'phone' ? 'tel' : kind === 'url' ? 'url' : 'text'}
          value={text}
          disabled={disabled}
          placeholder={placeholder ?? (kind === 'choice-text' ? 'Enter a value' : `Enter ${field.label}`)}
          className={invalidClass}
          onBlur={rhf.onBlur}
          onChange={(event) => {
            rhf.onChange(event.target.value);
          }}
          {...aria}
        />
      );
  }
}
